import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CONSTANTS } from "../../constants/Constants";
import { useGlobalContext } from "../../context/GlobalContext";
import LoadSpinner from "../../components/LoadingSpinner";
import Breadcrumb from "../../components/BreadCrumb";
import UsersTable from "../../components/UsersTable";
import Pagination from "../../components/Pagination";
import { getAllUsers, addUser, getRoles, getUserById, updateUser, deleteUser } from "../../services/api/users";
import { User, Role, CreateUserData, PaginationState, TableHeader } from "../../types/users";
import Input from "../../components/Inputs";
import Button from "../../components/Buttons";
import { usePagination } from "../../hooks/usePagination";
import searchIcon from "../../assets/icons/search_icon.svg";
import menuIcon from "../../assets/icons/menu_icon.svg";
import crossIcon from "../../assets/icons/cross_icon.svg";
import Select from "../../components/Select";
import Modal from "../../components/Modal";
import CreateUserForm from "../../components/CreateUserForm";
import { useToastContext } from "../../context/ToastContext";
import Swal from "sweetalert2";
import { convertToFormData, errorHandler } from "../../utils/helpers";

const Users: React.FC = () => {
    const { isLoading, setIsLoading, user } = useGlobalContext();
    const { showToast } = useToastContext();

    // Data
    const [usersList, setUsersList] = useState<User[]>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        totalCount: 0,
        pageSize: 10,
        currentPage: 1,
    });

    // UI State
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    // Form state
    const [userData, setUserData] = useState<CreateUserData>({
        name: "",
        email: "",
        image: null,
        selectedRole: null,
        status: "ENABLED",
    });
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [isLoadingUserData, setIsLoadingUserData] = useState(false);

    // Roles & edit
    const [roleOptions, setRoleOptions] = useState<Role[]>([]);
    const [isEdit, setIsEdit] = useState(false);
    const [editingUserId, setEditingUserId] = useState<string | null>(null);

    // Mobile menu
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Pagination helper hook
    const { pages, hasPrevious, hasNext, previous, next, goToPage } = usePagination(pagination);

    const fetchRoles = useCallback(async () => {
        try {
            const response = await getRoles();
            if (response?.data) setRoleOptions(response.data);
        } catch (err) {
            console.error("Failed to fetch roles:", err);
        }
    }, []);

    const fetchUsers = async ({ page, size = 10, search = "", role = "" }: { page: number; size: number; search?: string; role?: string }) => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await getAllUsers(page, size, search, role);

            if (response.success) {
                setUsersList(response.data || []);
                setPagination((prev) => ({
                    ...prev,
                    totalCount: response.pagination?.totalCount ?? 0,
                    pageSize: response.pagination?.pageSize ?? prev.pageSize,
                    currentPage: response.pagination?.currentPage ?? page,
                }));
            } else {
                setError(response.message || CONSTANTS.FAILED_TO_FETCH_USERS_TXT);
                setUsersList([]);
            }
        } catch (err: any) {
            setError(errorHandler(err) ?? CONSTANTS.FAILED_TO_FETCH_USERS_TXT);
            setUsersList([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch users on page change
    useEffect(() => {
        if (roleOptions.length === 0) {
            fetchRoles();
            setInitialLoadDone(true);
        }
        fetchUsers({ page: pagination.currentPage, size: pagination.pageSize, search: searchQuery, role: roleFilter });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.currentPage, pagination.pageSize]);

    // Debounced search
    useEffect(() => {
        if (!initialLoadDone) {
            return;
        }
        const id = setTimeout(() => {
            fetchUsers({ page: 1, size: pagination.pageSize, search: searchQuery, role: roleFilter });
        }, 500);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        // The debounced useEffect will handle the API call
    };

    const handleRoleFilterChange = (value: string) => {
        setRoleFilter(value);
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        fetchUsers({ page: 1, size: pagination.pageSize, search: searchQuery, role: value });
    };

    const tableHeaders: TableHeader[] = useMemo(
        () => [
            { key: "user", label: CONSTANTS.USER_TXT },
            { key: "role", label: CONSTANTS.ROLE_TXT },
            { key: "status", label: CONSTANTS.STATUS_TXT },
            { key: "actions", label: CONSTANTS.ACTION_TXT },
        ],
        []
    );

    const breadcrumbItems = useMemo(
        () => [
            { name: CONSTANTS.DASHBOARD_TXT, url: "/dashboard" },
            { name: CONSTANTS.USERS_TXT, url: "/users" },
        ],
        []
    );

    const resetForm = () => {
        setUserData({ name: "", email: "", image: null, selectedRole: null, status: "ENABLED" });
        setFieldErrors({});
        setIsEdit(false);
        setEditingUserId(null);
    };

    const openCreateModal = () => {
        resetForm();
        setIsCreateUserModalOpen(true);
    };

    const handleFileChange = (file: File | null) => {
        if (!file) return setUserData((prev) => ({ ...prev, image: null }));

        if (file.type.split("/")[0] !== "image") {
            setFieldErrors((prev) => ({ ...prev, Image: CONSTANTS.APP_ERRORS.ONLY_IMAGES_ERROR }));
            return;
        }

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setFieldErrors((prev) => ({ ...prev, Image: CONSTANTS.APP_ERRORS.IMAGE_SIZE_ERROR }));
            return;
        }

        setFieldErrors((prev) => ({ ...prev, Image: "" }));
        setUserData((prev) => ({ ...prev, image: file }));
    };

    const handleInputChange = (name: string, value: string) => {
        setUserData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (systemRoleId: string) => {
        const selected = roleOptions.find((r) => r.systemRoleId === systemRoleId) || null;
        setUserData((prev) => ({ ...prev, selectedRole: selected }));
    };

    // Generic change handler used by CreateUserForm
    const changeHandler = (e: Event | string, name?: string) => {
        if (typeof e !== "string") {
            const target = (e.target as HTMLInputElement) || ({} as HTMLInputElement);
            if (target.type === "file") {
                const file = target.files?.[0] ?? null;
                handleFileChange(file);
                return;
            }
            const inputName = target.name;
            if (inputName) {
                handleInputChange(inputName, target.value);
            }
        } else if (name === "userRoleId") {
            handleSelectChange(e);
        } else if (name === "status") {
            setUserData((prev) => ({ ...prev, status: e }));
        } else if (name) {
            handleInputChange(name, e);
        }
    };

    const handleFormSubmit = async () => {
        try {
            setIsModalLoading(true);

            const payload: any = {
                ...(isEdit && editingUserId ? { Id: editingUserId } : {}),
                Name: userData.name,
                Email: userData.email,
                ...(userData.image && { Image: userData.image }),
                UserRoleId: userData.selectedRole?.systemRoleId || "",
                Status: userData.status,
            };

            const formData = convertToFormData(payload);
            const apiCall = isEdit ? updateUser : addUser;
            const response = await apiCall(formData);

            if (response.success) {
                showToast(
                    response.message ?? (isEdit ? CONSTANTS.USER_UPDATED_SUCCESSFULLY_TXT : CONSTANTS.USER_CREATED_SUCCESSFULLY_TXT),
                    "success"
                );
                setIsCreateUserModalOpen(false);
                // refresh list: if adding, go to page 1; if editing, stay on same page
                await fetchUsers({ page: isEdit ? pagination.currentPage : 1, size: pagination.pageSize, search: searchQuery, role: roleFilter });
            } else {
                showToast(response.message || (isEdit ? CONSTANTS.ERROR_UPDATING_USER_TXT : CONSTANTS.ERROR_ADDING_USER_TXT), "error");
            }
        } catch (err: any) {
            const errorData = err?.response?.data;

            if (errorData?.errors) {
                const fieldErrorsObj: { [key: string]: string } = {};
                Object.keys(errorData.errors).forEach((field) => {
                    const serverVals = errorData.errors[field];
                    fieldErrorsObj[field] = Array.isArray(serverVals) ? serverVals.join(", ") : String(serverVals);
                });
                setFieldErrors(fieldErrorsObj);
                return;
            }

            const message = errorHandler(err) ?? (isEdit ? CONSTANTS.ERROR_UPDATING_USER_TXT : CONSTANTS.ERROR_ADDING_USER_TXT);
            showToast(message, "error");
            setFieldErrors({});
        } finally {
            setIsModalLoading(false);
        }
    };

    const handleEditUser = async (userId: string) => {
        try {
            setIsLoadingUserData(true);
            // Ensure roles are loaded before mapping
            if (!roleOptions.length) await fetchRoles();

            const response = await getUserById(userId);
            if (response.success) {
                const fetchedUser = response.data;

                const selectedRole = roleOptions.find((r) => r.systemRoleId === fetchedUser.roleId) || null;

                setUserData({
                    name: fetchedUser.name || "",
                    email: fetchedUser.email || "",
                    image: fetchedUser.image ?? null,
                    selectedRole,
                    status: (fetchedUser.status || "ENABLED").toUpperCase(),
                });

                setFieldErrors({});
                setIsEdit(true);
                setEditingUserId(userId);
                setIsCreateUserModalOpen(true);
            } else {
                showToast(response.message || CONSTANTS.FAILED_TO_LOAD_USER_DATA_TXT, "error");
            }
        } catch (err: any) {
            console.error("Failed to fetch user:", err);
            const message = errorHandler(err) ?? CONSTANTS.FAILED_TO_LOAD_USER_DATA_TXT;
            showToast(message, "error");
        } finally {
            setIsLoadingUserData(false);
        }
    };

    const handleDeleteUser = async (userId: string) => {
        const result = await Swal.fire({
            text: CONSTANTS.DELETE_USER_CONFIRMATION_TXT,
            toast: true,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: CONSTANTS.CONFIRM_TXT,
            customClass: {
                cancelButton: "text-gray-800 bg-gray-300 rounded-full text-sm w-[100px]",
                confirmButton: "rounded-full bg-black text-sm w-[100px]",
            },
        });

        if (!result.isConfirmed) return;

        try {
            setIsLoading(true);
            const response = await deleteUser(userId);

            if (response.success) {
                showToast(response.message ?? CONSTANTS.USER_DELETED_SUCCESSFULLY_TXT, "success");
                await fetchUsers({ page: pagination.currentPage, size: pagination.pageSize, search: searchQuery, role: roleFilter });
            } else {
                showToast(response.message || CONSTANTS.FAILED_TO_DELETE_USER_TXT, "error");
            }
        } catch (err: any) {
            console.error("Failed to delete user:", err);
            const message = errorHandler(err) ?? CONSTANTS.FAILED_TO_DELETE_USER_TXT;
            showToast(message, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const updatePage = (page: number) => setPagination((prev) => ({ ...prev, currentPage: page }));

    return (
        <div className="min-h-screen m-auto layout-container">
            <Breadcrumb path={breadcrumbItems} />

            {/* Search and Filters Section */}
            <div className="flex items-center">
                <div className="flex items-center justify-between gap-2 h-16 w-full">
                    <div className={`w-full border border-gray-300 rounded-3xl overflow-hidden px-3 flex ${isMenuOpen ? "hidden" : "flex"}`}>
                        <img src={searchIcon} alt="search_icon" className="mr-3" width={14} />
                        <Input
                            input_name={CONSTANTS.SEARCH_TXT}
                            placeholder={CONSTANTS.SEARCH_TXT}
                            type="text"
                            other_classes="px-0 w-full text-sm border-none !focus:outline-none"
                            value={searchQuery}
                            change_handler={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                        />
                    </div>

                    <div className={`flex font-medium gap-2 text-[13px] items-center ${isMenuOpen ? "flex w-[85%] overflow-x-auto" : "hidden md:flex"}`}>
                        <div className="w-32 relative inline-block">
                            <Select
                                name="roleFilter"
                                options={roleOptions.map((role) => ({ id: role.systemRoleName, name: role.systemRoleName }))}
                                handle_change={(e: React.ChangeEvent<HTMLSelectElement>) => handleRoleFilterChange(e.target.value)}
                                firstOptionText="All Roles"
                                value={roleFilter}
                            />
                        </div>

                        <Button
                            btn_label={CONSTANTS.CREATE_USER_TXT}
                            on_click_handler={openCreateModal}
                            other_classes={"!w-32 !h-9 text-[11px] md:hidden"}
                        />
                    </div>

                    <div>
                        <img
                            src={isMenuOpen ? crossIcon : menuIcon}
                            alt="menu_icon"
                            className={`block md:hidden cursor-pointer ml-2 ${isMenuOpen ? "w-8 h-8 bg-gray-200 p-2 rounded-full" : "w-6 h-6"}`}
                            width={24}
                            onClick={() => setIsMenuOpen((s) => !s)}
                        />
                    </div>
                </div>

                <Button
                    btn_label={CONSTANTS.CREATE_USER_TXT}
                    on_click_handler={openCreateModal}
                    other_classes={"!w-44 !h-11 text-[12px] hidden md:block ml-4"}
                />
            </div>

            {/* Users Table */}
            {isLoading ? (
                <div className="min-h-80 flex items-center justify-center">
                    <LoadSpinner />
                </div>
            ) : error ? (
                <div className="text-center content-center h-96">
                    <p className="text-red-500">{error}</p>
                </div>
            ) : (
                <div>
                    {usersList.length > 0 ? (
                        <div className="mt-4 relative">
                            {(isLoadingUserData || isLoading) && (
                                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                                    <LoadSpinner />
                                </div>
                            )}

                            <div className="overflow-x-auto">
                                <UsersTable
                                    tableHeader={tableHeaders}
                                    tableRows={usersList}
                                    onEdit={handleEditUser}
                                    onDelete={handleDeleteUser}
                                    currentUser={user}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="text-center content-center h-96">
                            {searchQuery || roleFilter ? (
                                <p>{CONSTANTS.NO_RESULTS_FOUND_TXT}</p>
                            ) : (
                                <p>{CONSTANTS.NO_USERS_FOUND_TXT}</p>
                            )}
                        </div>
                    )}

                    {/* Pagination */}
                    <div className="container mx-auto p-4">
                        {pages.length > 1 && (
                            <Pagination
                                pages={pages}
                                currentPage={pagination.currentPage}
                                hasPrevious={hasPrevious}
                                hasNext={hasNext}
                                onPrevious={() => {
                                    previous();
                                    updatePage(Math.max(1, pagination.currentPage - 1));
                                }}
                                onNext={() => {
                                    next();
                                    updatePage(Math.min(pages.length, pagination.currentPage + 1));
                                }}
                                onPageClick={(page) => {
                                    goToPage(page);
                                    updatePage(page);
                                }}
                            />
                        )}
                    </div>
                </div>
            )}

            {isCreateUserModalOpen && (
                <Modal
                    modal_handler={(open: boolean) => {
                        if (!open) setIsCreateUserModalOpen(false);
                        else openCreateModal();
                    }}
                    save_handler={handleFormSubmit}
                    modal_title={isEdit ? CONSTANTS.EDIT_USER_TXT : CONSTANTS.CREATE_USER_TXT}
                    is_btn_disable={!userData.name || !userData.email || !userData.selectedRole || isModalLoading}
                    other_classes="md:w-1/3 w-[80%]"
                >
                    <div className="relative">
                        {isModalLoading && (
                            <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10 rounded-lg">
                                <LoadSpinner />
                            </div>
                        )}

                        <CreateUserForm userData={userData} change_handler={changeHandler} fieldErrors={fieldErrors} roleOptions={roleOptions} isEdit={isEdit} />
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Users;